'use client';

import CloseIcon from 'assets/icons/close-x.svg';
import cn from 'classnames';
import ModalBackground from 'components/collection/modal-viewer/modal-background';
import ClientPortal from 'components/helper/client-portal';
import RichTextInline from 'components/rich-text/rich-text-inline-only';
import type { SanityFootnote } from '@metmuseum/types';
import tooltipStyles from 'components/tooltip/tooltip.module.scss';
import useMediaQuery from 'hooks/use-media-query';
import { BREAKPOINT_M } from 'lib/constants.mjs';
import {
	useEffect,
	useId,
	useRef,
	useState,
	type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { ArrowContainer, Popover } from 'react-tiny-popover';
import styles from './footnote.module.scss';

// We use this to ensure that the footnote marker stays on the same line as the preceding word.
const WORD_JOINER = '\u2060';

// Note that this interface doens't actually match the value that is returned
// by the Sanity. We close over the position in the EditorialRichText component
// after we calculate their positions. Since this is a client-side component,
// this felt like the most straightforward approach.
export default function Footnote({
	value,
	position,
}: {
	value: SanityFootnote;
	// 1-based index representing the order in which the footnotes are
	// found in the body of an EditorialRichText.
	position: number;
}) {
	const { matches: isMobile } = useMediaQuery(`(max-width: ${BREAKPOINT_M}px)`);
	const contentId = useId();
	const [modalOpen, setModalOpen] = useState(false);
	const footnoteRef = useRef<HTMLElement>(null);
	const modalRef = useRef(null);
	const prevModalOpen = useRef(false);

	const closeModal = () => {
		setModalOpen(false);
	};

	const toggleModalOpen = () => {
		setModalOpen((isOpen) => !isOpen);
	};

	// Handle key down events when the toggle is focused.
	const handleToggleKeyPopoverTarget = (
		event: ReactKeyboardEvent<HTMLElement>,
	) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			toggleModalOpen();
		}
	};

	// Close modal if the click target is outside the modal container
	const handleModalClickOutside = (event) => {
		if (modalRef.current && !modalRef.current.contains(event.target)) {
			closeModal();
		}
	};

	const label = `Footnote ${position}`;

	// Escape closes the modal when it's open regardless of the current focus.
	useEffect(() => {
		if (!modalOpen) return;

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key !== 'Escape') return;
			closeModal();
		};

		document.addEventListener('keydown', handleKeyDown);

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [modalOpen]);

	// Refocus the trigger element when the popup closes.
	useEffect(() => {
		if (prevModalOpen.current && !modalOpen) {
			footnoteRef.current?.focus();
		}

		prevModalOpen.current = modalOpen;
	}, [modalOpen]);

	return (
		<>
			<Popover
				ref={footnoteRef}
				padding={4}
				containerClassName={tooltipStyles.tooltip}
				onClickOutside={closeModal}
				isOpen={modalOpen && !isMobile}
				positions={['bottom', 'right', 'left', 'top']} // preferred positions by priority
				content={({ position, childRect, popoverRect }) => (
					<ArrowContainer
						position={position}
						childRect={childRect}
						popoverRect={popoverRect}
						arrowColor={'var(--color-site-background)'}
						arrowSize={10}
					>
						<div
							className={tooltipStyles.content}
							id={contentId}
							data-testid="footnote-content-popover"
						>
							<RichTextInline value={value.body} />
						</div>
					</ArrowContainer>
				)}
			>
				{/* The <sup /> element prevents word wrapping in most cases, but it doesn't prevent a line break if the
					  the preceding character is punctuation. Stylistically, footnote markers should occur after punctuation, so we
					  need to handle this case. A word joiner (U+2060) character handles this case. Note that line breaks can still
					  (correctly) occur if the preceding character is whitespace. */}
				<sup
					className={cn('button', styles.footnote)}
					role="button"
					tabIndex={0}
					onClick={toggleModalOpen}
					onKeyDown={handleToggleKeyPopoverTarget}
					aria-label={label}
					aria-haspopup="dialog"
					aria-expanded={modalOpen}
					aria-controls={modalOpen ? contentId : undefined}
					aria-describedby={!isMobile && modalOpen ? contentId : undefined}
					// This points to the corresponding footnote in the glossary
					// at the end of an editorial piece.
					aria-details={`footnote-${value._key}`}
				>
					{WORD_JOINER}[{position}]
				</sup>
			</Popover>
			{modalOpen && isMobile ? (
				<ClientPortal>
					<ModalBackground
						onClick={handleModalClickOutside}
						mode="subtle"
						aria-label={label}
						id={contentId}
					>
						<div className={styles.modal} ref={modalRef}>
							<button
								type="button"
								className={styles.close}
								onClick={closeModal}
								aria-label="Close footnote"
							>
								<CloseIcon />
							</button>
							<RichTextInline value={value.body} />
						</div>
					</ModalBackground>
				</ClientPortal>
			) : null}
		</>
	);
}
